// Editor for a progress report's builder-entered parts: milestones with planned
// and actual dates, progress by line, the issues log and capture details.
// view (the saved report's view) numbers evidence as E refs and lists the
// range's visits and photos; a new report has no view yet, so refs, photos and
// per-visit capture details are added after the first save.
import { F, Btn } from '../../lib/bricks';
import {
  MAX_MILESTONES, MAX_SOV_LINES, MAX_ISSUES, CONTROL_OPTIONS, blankLine, blankIssue, blankMilestone, dayLabel,
} from './progressReportModel';

const MUTED = 'var(--c-ink-mute)';
const LINE = `1px solid var(--c-hair-s)`;
export const field = { padding: '10px 12px', minHeight: 44, border: '1px solid var(--c-line)', borderRadius: 2, fontSize: 14, minWidth: 0, width: '100%', boxSizing: 'border-box', fontFamily: F.display, background: 'var(--c-field)', color: 'var(--c-ink)' };
export const label = { display: 'block', fontSize: 12, color: MUTED, marginBottom: 6 };
const quiet = { minHeight: 44, padding: '0 14px', fontSize: 13 };
const removeBtn = { minWidth: 44, minHeight: 44, background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 13, padding: '0 4px', fontFamily: F.display };

export const DETAILS_CSS = `
.prd-group{border-top:${LINE};padding-top:16px;display:flex;flex-direction:column;gap:12px;min-width:0}
.prd-h{font-size:14px;font-weight:500}
.prd-p{margin:0;font-size:12.5px;color:${MUTED};line-height:1.5}
.prd-item{border-top:1px solid var(--c-hair);padding-top:12px;display:grid;gap:10px 12px;min-width:0}
.prd-ms{grid-template-columns:minmax(0,1fr) 150px 150px auto auto;align-items:end}
.prd-ln{grid-template-columns:48px minmax(0,2fr) minmax(0,1fr) 88px minmax(0,1fr) auto;align-items:end}
.prd-ln .prd-note{grid-column:2 / -2}
.prd-is{grid-template-columns:48px minmax(0,2fr) minmax(0,1.2fr) 120px auto;align-items:end}
.prd-is .prd-sub{grid-column:2 / -2;display:grid;grid-template-columns:minmax(0,1fr) 150px 150px;gap:10px 12px}
.prd-is .prd-note{grid-column:2 / -2}
.prd-cap{grid-template-columns:120px minmax(0,1.4fr) minmax(0,1.2fr) 96px minmax(0,1.3fr) 88px;align-items:end}
.prd-no{font-family:${F.mono};font-size:12.5px;color:var(--c-ink);min-height:44px;display:flex;align-items:center}
.prd-chk{display:inline-flex;align-items:center;gap:6px;font-size:13px;min-height:44px;white-space:nowrap}
@media (max-width:719px){
  .prd-ms{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}
  .prd-ms .prd-wide{grid-column:1 / -1}
  .prd-ln,.prd-is{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}
  .prd-ln>*,.prd-is>*{grid-column:auto}
  .prd-ln .prd-wide,.prd-is .prd-wide,.prd-ln .prd-note,.prd-is .prd-note,.prd-is .prd-sub{grid-column:1 / -1}
  .prd-is .prd-sub{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}
  .prd-is .prd-sub .prd-wide{grid-column:1 / -1}
  .prd-cap{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}
  .prd-cap .prd-wide{grid-column:1 / -1}
  .prd-no{min-height:0}
}
`;

const at = (list, i, patch) => list.map((x, j) => (j === i ? { ...x, ...patch } : x));
const without = (list, i) => list.filter((_, j) => j !== i);

export function MilestonesEditor({ milestones, onChange }) {
  return (
    <div className="prd-group" role="group" aria-labelledby="prd-ms-h" data-testid="milestones-editor">
      <span id="prd-ms-h" className="prd-h">Milestones</span>
      <p className="prd-p">Planned and actual dates are yours. Leave them blank if you don’t track them.</p>
      {milestones.map((m, i) => (
        <div key={i} className="prd-item prd-ms">
          <div className="prd-wide"><label style={label} htmlFor={`prd-ms-${i}`}>Milestone {i + 1}</label>
            <input id={`prd-ms-${i}`} aria-label={`Milestone ${i + 1}`} value={m.text} onChange={(e) => onChange(at(milestones, i, { text: e.target.value }))} maxLength={160} style={field} /></div>
          <div><label style={label} htmlFor={`prd-msp-${i}`}>Planned</label>
            <input id={`prd-msp-${i}`} type="date" value={m.planned || ''} onChange={(e) => onChange(at(milestones, i, { planned: e.target.value }))} style={field} /></div>
          <div><label style={label} htmlFor={`prd-msa-${i}`}>Actual</label>
            <input id={`prd-msa-${i}`} type="date" value={m.actual || ''} onChange={(e) => onChange(at(milestones, i, { actual: e.target.value, done: e.target.value ? true : m.done }))} style={field} /></div>
          <label className="prd-chk"><input type="checkbox" checked={!!m.done} onChange={(e) => onChange(at(milestones, i, { done: e.target.checked }))} /> Done</label>
          <button type="button" aria-label={`Remove milestone ${i + 1}`} onClick={() => onChange(without(milestones, i))} style={removeBtn}>Remove</button>
        </div>
      ))}
      {milestones.length < MAX_MILESTONES ? (
        <div><Btn variant="ghost" type="button" style={quiet} onClick={() => onChange([...milestones, blankMilestone()])}>Add milestone</Btn></div>
      ) : null}
    </div>
  );
}

export function LinesEditor({ lines, onChange, view }) {
  const refs = view && (view.evidence || []).some((e) => e.ref && e.did);
  return (
    <div className="prd-group" role="group" aria-labelledby="prd-ln-h" data-testid="lines-editor">
      <span id="prd-ln-h" className="prd-h">Progress by line</span>
      <p className="prd-p">
        Your schedule of values, line by line. The report prints each % as reported by you, with the evidence you point to.
        {refs ? ' Evidence is the E numbers from the evidence index, e.g. E03, E07.' : ' Save the report first to point lines at evidence.'}
      </p>
      {lines.map((l, i) => (
        <div key={i} className="prd-item prd-ln" data-testid="line-row">
          <span className="prd-no" aria-hidden>{`L${String(i + 1).padStart(2, '0')}`}</span>
          <div className="prd-wide"><label style={label} htmlFor={`prd-ln-${i}`}>Line</label>
            <input id={`prd-ln-${i}`} value={l.name} onChange={(e) => onChange(at(lines, i, { name: e.target.value }))} maxLength={160} placeholder="e.g. Foundations" style={field} /></div>
          <div><label style={label} htmlFor={`prd-lv-${i}`}>Value ($, optional)</label>
            <input id={`prd-lv-${i}`} inputMode="decimal" value={l.value} onChange={(e) => onChange(at(lines, i, { value: e.target.value }))} placeholder="e.g. 640,000" style={field} /></div>
          <div><label style={label} htmlFor={`prd-lp-${i}`}>% complete</label>
            <input id={`prd-lp-${i}`} inputMode="numeric" value={l.percent} onChange={(e) => onChange(at(lines, i, { percent: e.target.value }))} placeholder="0–100" style={field} /></div>
          {refs ? (
            <div><label style={label} htmlFor={`prd-lr-${i}`}>Evidence</label>
              <input id={`prd-lr-${i}`} value={l.refs} onChange={(e) => onChange(at(lines, i, { refs: e.target.value }))} placeholder="E03, E07" autoCapitalize="characters" style={{ ...field, fontFamily: F.mono }} /></div>
          ) : <span />}
          <button type="button" aria-label={`Remove line ${i + 1}`} onClick={() => onChange(without(lines, i))} style={removeBtn}>Remove</button>
          <div className="prd-note"><label style={label} htmlFor={`prd-ln-n-${i}`}>Note (optional)</label>
            <input id={`prd-ln-n-${i}`} value={l.note} onChange={(e) => onChange(at(lines, i, { note: e.target.value }))} maxLength={160} style={field} /></div>
        </div>
      ))}
      {lines.length < MAX_SOV_LINES ? (
        <div><Btn variant="ghost" type="button" style={quiet} onClick={() => onChange([...lines, blankLine()])} data-testid="add-line">Add line</Btn></div>
      ) : null}
    </div>
  );
}

const photoOptions = (view) => (view ? (view.evidence || []).filter((e) => e.kind === 'stills' && e.did) : []);

export function IssuesEditor({ issues, onChange, view }) {
  const photos = photoOptions(view);
  return (
    <div className="prd-group" role="group" aria-labelledby="prd-is-h" data-testid="issues-editor">
      <span id="prd-is-h" className="prd-h">Issues and observations</span>
      <p className="prd-p">Anything a lender should know about. Each keeps its I number from report to report until you close it.</p>
      {issues.map((x, i) => (
        <div key={i} className="prd-item prd-is" data-testid="issue-row">
          <span className="prd-no" aria-hidden>{x.ref || 'New'}</span>
          <div className="prd-wide"><label style={label} htmlFor={`prd-it-${i}`}>Issue or observation</label>
            <input id={`prd-it-${i}`} value={x.title} onChange={(e) => onChange(at(issues, i, { title: e.target.value }))} maxLength={160} placeholder="e.g. Ponding at the north entrance" style={field} /></div>
          <div className="prd-wide"><label style={label} htmlFor={`prd-il-${i}`}>Location</label>
            <input id={`prd-il-${i}`} value={x.location} onChange={(e) => onChange(at(issues, i, { location: e.target.value }))} maxLength={160} placeholder="e.g. Grid C4" style={field} /></div>
          <div><label style={label} htmlFor={`prd-is-${i}`}>Status</label>
            <select id={`prd-is-${i}`} value={x.status} onChange={(e) => onChange(at(issues, i, { status: e.target.value }))} style={field}>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select></div>
          <button type="button" aria-label={`Remove issue ${i + 1}`} onClick={() => onChange(without(issues, i))} style={removeBtn}>Remove</button>
          <div className="prd-sub">
            {photos.length ? (
              <div className="prd-wide"><label style={label} htmlFor={`prd-ip-${i}`}>Photo</label>
                <select id={`prd-ip-${i}`} value={x.photo || ''} onChange={(e) => onChange(at(issues, i, { photo: e.target.value }))} style={field}>
                  <option value="">No photo</option>
                  {photos.map((p) => <option key={p.did} value={p.did}>{`${p.ref} · ${dayLabel(p.date)} · ${p.label}`}</option>)}
                </select></div>
            ) : <span className="prd-wide" />}
            <div><label style={label} htmlFor={`prd-io-${i}`}>Opened</label>
              <input id={`prd-io-${i}`} type="date" value={x.opened || ''} onChange={(e) => onChange(at(issues, i, { opened: e.target.value }))} style={field} /></div>
            {x.status === 'closed' ? (
              <div><label style={label} htmlFor={`prd-ic-${i}`}>Closed</label>
                <input id={`prd-ic-${i}`} type="date" value={x.closed || ''} onChange={(e) => onChange(at(issues, i, { closed: e.target.value }))} style={field} /></div>
            ) : <span />}
          </div>
          <div className="prd-note"><label style={label} htmlFor={`prd-in-${i}`}>Note (optional)</label>
            <textarea id={`prd-in-${i}`} value={x.note} onChange={(e) => onChange(at(issues, i, { note: e.target.value }))} maxLength={600} rows={2} style={{ ...field, resize: 'vertical' }} /></div>
        </div>
      ))}
      {issues.length < MAX_ISSUES ? (
        <div><Btn variant="ghost" type="button" style={quiet} onClick={() => onChange([...issues, blankIssue()])} data-testid="add-issue">Add issue</Btn></div>
      ) : null}
    </div>
  );
}

const blankCap = { aircraft: '', pilot_cert: '', altitude_m: '', control: '', gcp_count: '' };

// Per visit: what the files don't say. Rows the files already fill show as placeholders.
export function CaptureEditor({ capture, onChange, view }) {
  const known = {};
  ((view && view.capture) || []).forEach((r) => { known[r.visit_id] = r; });
  const rows = ((view && view.visits) || []).filter((v) => v.id).map((v) => ({ visit_id: v.id, no: v.no, date: v.date, ...(known[v.id] || {}) }));
  if (!rows.length) return null;
  const set = (vid, patch) => onChange({ ...capture, [vid]: { ...blankCap, ...(capture[vid] || {}), ...patch } });
  return (
    <div className="prd-group" role="group" aria-labelledby="prd-cap-h" data-testid="capture-editor">
      <span id="prd-cap-h" className="prd-h">Capture details</span>
      <p className="prd-p">Fill in what the files don’t say. Pilot certificates print as the level and the last four digits only.</p>
      {rows.map((r) => {
        const c = { ...blankCap, ...(capture[r.visit_id] || {}) };
        const id = (k) => `prd-c-${r.visit_id}-${k}`;
        return (
          <div key={r.visit_id} className="prd-item prd-cap" data-testid="capture-row">
            <span className="prd-no prd-wide">{`Visit ${r.no} · ${dayLabel(r.date)}`}</span>
            <div><label style={label} htmlFor={id('a')}>Aircraft</label>
              <input id={id('a')} value={c.aircraft} onChange={(e) => set(r.visit_id, { aircraft: e.target.value })} maxLength={80} placeholder={r.aircraft_from === 'files' ? r.aircraft : 'e.g. DJI Mavic 3E'} style={field} /></div>
            <div><label style={label} htmlFor={id('p')}>Pilot certificate</label>
              <input id={id('p')} value={c.pilot_cert} onChange={(e) => set(r.visit_id, { pilot_cert: e.target.value })} maxLength={80} placeholder={r.pilot ? 'On file' : 'e.g. Advanced PA-1234'} style={field} /></div>
            <div><label style={label} htmlFor={id('h')}>Height (m)</label>
              <input id={id('h')} inputMode="decimal" value={c.altitude_m} onChange={(e) => set(r.visit_id, { altitude_m: e.target.value })} placeholder={r.height_m ? String(r.height_m) : 'e.g. 60'} style={field} /></div>
            <div><label style={label} htmlFor={id('c')}>Control</label>
              <select id={id('c')} value={c.control} onChange={(e) => set(r.visit_id, { control: e.target.value })} style={field}>
                {CONTROL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select></div>
            {c.control === 'gcp' ? (
              <div><label style={label} htmlFor={id('g')}>GCPs</label>
                <input id={id('g')} inputMode="numeric" value={c.gcp_count} onChange={(e) => set(r.visit_id, { gcp_count: e.target.value })} placeholder="e.g. 6" style={field} /></div>
            ) : <span />}
          </div>
        );
      })}
    </div>
  );
}
